import React from 'react'
import { getRootClassNames, getSwanBaseUrl, SwanProvider, type Translations } from '@vp/swan'
import { StyleContextProvider } from '@vp/ubik-context'
import { createUserContext } from './create-user-context'
import type { FragmentRootOptions, TenantMapping, UserContext } from '@vp/ubik-fragment-types'
import { RootProviders, createRootOptions } from '../common'
import { webStyleRegistry } from './style-registry'
import { jsonImport } from '../common/json-cache'
import type { SwanBrand } from '../common/swan-types'

export type CreateRootProviderOptions = {
  /** User context. If not provided, falls back to reading from the DOM (#ubik-user-context script tag). */
  userContext?: UserContext
  rootOptions?: FragmentRootOptions
  /** Tenant mappings to surface via TenantMappingProvider. When undefined, the
   *  Provider stack omits the TenantMappingProvider. The swan brand (if any) is
   *  read from here. */
  tenantMappings?: TenantMapping
}

export type CreateRootProviderResult = {
  /** A Provider component that wraps children with the full client provider stack (ErrorBoundary, StrictMode, Logger, UserContext, Style, Swan). The Swan layer wraps children in the brand-aware swan root `<div>`. */
  Provider: React.FC<{ children?: React.ReactNode }>
}

const isSwanBrand = (brand: string | undefined): brand is SwanBrand => {
  return !!brand
}

/**
 * Creates a Provider component that wraps children with the standard Ubik
 * client provider stack (ErrorBoundary, StrictMode, LoggerProvider,
 * UserContextProvider, StyleContextProvider, SwanProvider) without
 * performing hydration.
 *
 * This is useful for scenarios where you need the full provider context
 * around a component tree without calling `hydrateRoot` — for example,
 * integration with third-party rendering pipelines or testing.
 *
 * @example
 * ```tsx
 * const { Provider } = await createRootProvider({
 *   userContext: { locale: 'en-US', tenant: 'vistaprint' },
 *   rootOptions: { strictMode: true },
 * })
 * // use <Provider> to wrap your tree
 * ```
 */
export async function createRootProvider(options: CreateRootProviderOptions = {}): Promise<CreateRootProviderResult> {
  const rootOptions = createRootOptions(options.rootOptions)
  const userContext = options.userContext ?? createUserContext()
  const locale = userContext.locale

  const styleRegistry = webStyleRegistry()
  const swanTranslations = await jsonImport<Translations>(`@vp/swan/i18n/${locale.toLowerCase()}.json`)
  const swanBaseUrl = getSwanBaseUrl(userContext.tenant, locale)
  const tenantMappingSwan = options.tenantMappings?.swan
  const swanBrand = isSwanBrand(tenantMappingSwan) ? tenantMappingSwan : 'vistaprint'
  const swanRootClassName = getRootClassNames({ brand: swanBrand })

  const Provider: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
    <RootProviders rootOptions={rootOptions} userContext={userContext} trackedUserContext={[]} tenantMappings={options.tenantMappings}>
      <StyleContextProvider value={styleRegistry}>
        <SwanProvider brand={swanBrand} swanBaseUrl={swanBaseUrl} swanLocale={locale} translations={swanTranslations}>
          <div className={swanRootClassName}>{children}</div>
        </SwanProvider>
      </StyleContextProvider>
    </RootProviders>
  )

  return { Provider }
}
